/**
 * Standalone Question Bank screen (NA-197): lists existing bank questions
 * across all four types and lets an author create a new one independent of
 * lesson authoring. Browse controls are server-driven and bounded to one page.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCircleExclamation, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { useQueryClient } from '@tanstack/react-query';
import type {
  QuestionBlockType,
  QuestionDefinitionDto,
  QuestionListItem,
  PublishJobDto,
  PublishJobSummary,
} from '@course-authoring/shared-types';

import {
  EMPTY_QUESTION_USAGE,
  useArchiveQuestionMutation,
  useDeleteQuestionMutation,
  useQuestionPage,
  useQuestionUsageQuery,
  useUnarchiveQuestionMutation,
  isPublishJobConflict,
  fetchQuestionPublishJob,
  questionKeys,
} from '../api/questionApi';
import { questionBlockTypeLabel } from '../components/questionBank/questionBlockConversion';
import { QuestionCreatePanel } from '../components/questionBank/QuestionCreatePanel';
import { QuestionUsagePanel } from '../components/questionBank/QuestionUsagePanel';
import { ListPagination } from '../components/table/ListPagination';
import { SortableHeader } from '../components/table/SortableHeader';
import { useListControls, usePageBoundsReset } from '../hooks/useListControls';
import { formatQuestionHumanId } from '../utils/humanIds';
import { PUBLISH_JOB_POLL_INTERVAL_MS } from '../utils/publishJobPolling';
import {
  AdminIcon,
  AdminPageHeader,
  AdminRowMenu,
  adminActionIcons,
  adminButton,
  adminTokens,
  createAdminListStyles,
  useAdminNarrowViewport,
} from '../admin-ui';
import { useLanguageContext } from '../hooks/useLanguageContext';
import { TranslationStatusBadge } from '../components/TranslationStatusPrimitives';
import { toScopeAccountId, useClientContext } from '../hooks/useClientContext';
import { getAccountLabel } from '../components/layout/ClientContextPicker';
import { isActivePublishJob } from '@course-authoring/ui-blocks';
import { deriveCurrentPublishJobProgress } from '@course-authoring/ui-blocks';

/**
 * `@course-authoring/shared-types` re-exports a `QUESTION_BLOCK_TYPES` constant,
 * but it's `blockMetadata.ts`'s copy — typed as the broader `AuthoringBlockType`
 * union (it also backs the lesson block-type chooser) — not the correctly
 * `QuestionBlockType[]`-typed one in `questionBank.ts`, which the index
 * deliberately doesn't re-export to avoid the name clash. Same five members,
 * but only this local copy type-checks against `QuestionBlockType`-typed APIs.
 */
const QUESTION_TYPES: readonly QuestionBlockType[] = [
  'likert-scale',
  'single-choice-answer',
  'workplace-color-spectrum',
  'categorization',
  'freeform-text',
];

type SortColumn = 'id' | 'questionText' | 'type' | 'public' | 'usedIn';

const QUESTION_SORT_KEYS: readonly SortColumn[] = ['id', 'questionText', 'type', 'public', 'usedIn'];

const QUESTION_TYPE_OPTIONS: Array<{ value: QuestionBlockType | 'all'; label: string }> = [
  { value: 'all', label: 'All types' },
  ...QUESTION_TYPES.map((blockType) => ({ value: blockType, label: questionBlockTypeLabel(blockType) })),
];

function currentRowJob(
  listed: PublishJobSummary | null | undefined,
  observed: PublishJobSummary | null | undefined,
): PublishJobSummary | null | undefined {
  if (!observed) return listed;
  if (!listed) return observed;
  if (listed.id !== observed.id) return listed.id > observed.id ? listed : observed;
  if (listed.attempt !== observed.attempt) return listed.attempt > observed.attempt ? listed : observed;
  const rank = { queued: 0, running: 1, completed: 2, failed: 2 };
  return rank[listed.status] > rank[observed.status] ? listed : observed;
}

function toRowJobSummary(job: PublishJobSummary | PublishJobDto | null): PublishJobSummary | null {
  if (!job) return null;
  const progress = 'stepResults' in job ? deriveCurrentPublishJobProgress(job) : job.progress;
  return {
    id: job.id,
    status: job.status,
    currentStep: job.currentStep,
    attempt: job.attempt,
    ...(progress ? { progress } : {}),
  };
}

export function QuestionsPage() {
  const queryClient = useQueryClient();
  const isNarrow = useAdminNarrowViewport();
  const { selectedLocale, selectedLanguageLabel, languageOptions } = useLanguageContext();
  const { clientContext } = useClientContext();
  const scopeAccountId = toScopeAccountId(clientContext?.id);
  const scopeAccountName = clientContext && scopeAccountId !== null ? getAccountLabel(clientContext) : null;
  const styles = useMemo(() => createStyles(isNarrow), [isNarrow]);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const rowMenuRef = useRef<HTMLDivElement | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hoveredType, setHoveredType] = useState<QuestionBlockType | null>(null);
  const [createType, setCreateType] = useState<QuestionBlockType | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<QuestionListItem | null>(null);
  const [createdEditor, setCreatedEditor] = useState(false);
  const [openRowMenuId, setOpenRowMenuId] = useState<number | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<QuestionDefinitionDto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<QuestionDefinitionDto | null>(null);
  const [usageDialogQuestion, setUsageDialogQuestion] = useState<QuestionDefinitionDto | null>(null);
  const [duplicateTarget, setDuplicateTarget] = useState<QuestionDefinitionDto | null>(null);
  const [hoveredRowMenuItemId, setHoveredRowMenuItemId] = useState<string | null>(null);
  const [rowJobs, setRowJobs] = useState<Record<number, PublishJobSummary | null>>({});
  const rowJobsRef = useRef<Record<number, PublishJobSummary | null>>({});
  const refreshedTerminalAttemptsRef = useRef<Set<string>>(new Set());
  const [rowActionError, setRowActionError] = useState<string | null>(null);
  const [rowStatusWarning, setRowStatusWarning] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<QuestionBlockType | 'all'>('all');
  const [showArchived, setShowArchived] = useState(false);
  const {
    searchInput,
    setSearchInput,
    sortKey,
    sortDir,
    handleSort,
    page,
    setPage,
    pageSize,
    setPageSize,
    queryParams,
    hasNonDefaultState,
    resetToDefaults,
  } = useListControls<SortColumn>({
    defaultSort: 'id',
    sortKeys: QUESTION_SORT_KEYS,
    sortStorageKey: '/questions/manage',
  });

  const hasFiltersApplied = hasNonDefaultState || typeFilter !== 'all' || showArchived;
  const handleResetFilters = () => {
    resetToDefaults();
    setTypeFilter('all');
    setShowArchived(false);
  };

  const listParams = useMemo(() => ({
    page,
    pageSize,
    search: queryParams.search,
    sort: sortKey,
    order: sortDir,
    ...(typeFilter === 'all' ? {} : { blockType: typeFilter }),
    ...(showArchived ? { includeArchived: true } : {}),
    locale: selectedLocale,
    externalAccountId: scopeAccountId,
  }), [page, pageSize, queryParams.search, sortKey, sortDir, typeFilter, showArchived, selectedLocale, scopeAccountId]);
  const {
    data: questionPage,
    isLoading,
    isError,
    isPlaceholderData,
    refetch: refetchQuestionPage,
  } = useQuestionPage(listParams);
  const questions: QuestionListItem[] = questionPage?.items ?? [];
  const totalCount = questionPage?.totalCount ?? 0;

  // NA-403 review fix: `keepPreviousData` (in useQuestionPage) means switching
  // Content Context keeps rendering the *previous* scope's rows as
  // placeholder data while the new scope's fetch is in flight — but the page
  // title above is derived directly from clientContext and updates
  // immediately, so the old rows would briefly show under the new title.
  // Track the scope the currently-displayed data actually belongs to, and
  // treat a scope mismatch as still-loading rather than rendering stale rows.
  const confirmedScopeAccountIdRef = useRef(scopeAccountId);
  useEffect(() => {
    if (!isPlaceholderData) confirmedScopeAccountIdRef.current = scopeAccountId;
  }, [isPlaceholderData, scopeAccountId]);
  const isSwitchingScope = isPlaceholderData && confirmedScopeAccountIdRef.current !== scopeAccountId;

  usePageBoundsReset({ page, pageSize, totalCount, isPlaceholderData, setPage });

  useEffect(() => {
    setPage(1);
    setEditingQuestion(null);
  }, [selectedLocale, scopeAccountId, setPage]);

  useEffect(() => {
    if (isPlaceholderData) setOpenRowMenuId(null);
  }, [isPlaceholderData]);

  const onJobStatusChange = useCallback((questionId: number, job: PublishJobSummary | PublishJobDto | null) => {
    const summary = toRowJobSummary(job);
    const current = rowJobsRef.current[questionId];
    const merged = currentRowJob(current, summary) ?? null;
    if (current !== merged) {
      rowJobsRef.current = { ...rowJobsRef.current, [questionId]: merged };
      setRowJobs(rowJobsRef.current);
    }
    if (summary && merged && merged.id === summary.id && merged.attempt === summary.attempt
      && (merged.status === 'completed' || merged.status === 'failed')) {
      const attemptKey = `${summary.id}:${summary.attempt}`;
      if (!refreshedTerminalAttemptsRef.current.has(attemptKey)) {
        refreshedTerminalAttemptsRef.current.add(attemptKey);
        void queryClient.invalidateQueries({ queryKey: questionKeys.all });
      }
    }
  }, [queryClient]);

  useEffect(() => {
    if (openRowMenuId === null) return;
    const row = questions.find((question) => question.questionDefinitionId === openRowMenuId);
    const job = currentRowJob(row?.publishJob, rowJobs[openRowMenuId]);
    if (isActivePublishJob(job)) setOpenRowMenuId(null);
  }, [openRowMenuId, questions, rowJobs]);

  useEffect(() => {
    const activeIds = questions
      .filter((question) => isActivePublishJob(currentRowJob(question.publishJob, rowJobs[question.questionDefinitionId])))
      .map((question) => question.questionDefinitionId);
    if (activeIds.length === 0) return;
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      void Promise.all(activeIds.map(async (id) => {
        try {
          const job = await fetchQuestionPublishJob(id);
          onJobStatusChange(id, job);
          setRowStatusWarning(null);
        } catch {
          setRowStatusWarning('Preparation status could not be refreshed. The last known state is retained.');
        }
      }));
    }, PUBLISH_JOB_POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [questions, rowJobs, onJobStatusChange]);

  const archiveMutation = useArchiveQuestionMutation();
  const deleteMutation = useDeleteQuestionMutation();
  const unarchiveMutation = useUnarchiveQuestionMutation();

  useEffect(() => {
    if (!isMenuOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  useEffect(() => {
    if (openRowMenuId === null) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (rowMenuRef.current && !rowMenuRef.current.contains(e.target as Node)) {
        setOpenRowMenuId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openRowMenuId]);

  const handleSelectType = (blockType: QuestionBlockType) => {
    setIsMenuOpen(false);
    setCreateType(blockType);
  };

  const handlePanelClose = () => setCreateType(null);
  const resetDefaultBrowsePageAfterCreate = () => {
    const isDefaultBrowse = !queryParams.search
      && typeFilter === 'all'
      && !showArchived
      && sortKey === 'id'
      && sortDir === 'desc';
    if (isDefaultBrowse) setPage(1);
  };
  const handleCreated = (question: QuestionDefinitionDto & { publishJob?: PublishJobSummary | null }) => {
    setCreateType(null);
    setEditingQuestion({ ...question, usageCount: 0, responseCount: 0, taxonomyTags: [], concepts: [] });
    setCreatedEditor(true);
    resetDefaultBrowsePageAfterCreate();
  };
  const handleDuplicateCreated = (question: QuestionDefinitionDto & { publishJob?: PublishJobSummary | null }) => {
    setDuplicateTarget(null);
    setEditingQuestion({ ...question, usageCount: 0, responseCount: 0, taxonomyTags: [], concepts: [] });
    setCreatedEditor(true);
    resetDefaultBrowsePageAfterCreate();
  };

  const handleEditQuestion = (question: QuestionListItem) => {
    setOpenRowMenuId(null);
    setCreatedEditor(false);
    setEditingQuestion(question);
  };

  const handleDuplicateQuestion = (question: QuestionListItem) => {
    setOpenRowMenuId(null);
    setDuplicateTarget(question.englishSource ?? question);
  };

  const handleUsageCellClick = (question: QuestionDefinitionDto) => {
    setUsageDialogQuestion(question);
  };

  const handleConfirmArchive = async () => {
    if (!archiveTarget) return;
    try {
      await archiveMutation.mutateAsync(archiveTarget.questionDefinitionId);
      setArchiveTarget(null);
    } catch (error) {
      await handleRowActionError(error, archiveTarget.questionDefinitionId);
      setArchiveTarget(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.questionDefinitionId);
      setDeleteTarget(null);
    } catch (error) {
      await handleRowActionError(error, deleteTarget.questionDefinitionId);
      setDeleteTarget(null);
    }
  };

  // No confirmation for Unarchive (NA-196) — the ticket specifies
  // confirmation copy only for Archive and Delete.
  const handleUnarchiveQuestion = async (question: QuestionDefinitionDto) => {
    setOpenRowMenuId(null);
    try {
      await unarchiveMutation.mutateAsync(question.questionDefinitionId);
    } catch (error) {
      await handleRowActionError(error, question.questionDefinitionId);
    }
  };

  const handleRowActionError = async (error: unknown, questionId: number) => {
    setOpenRowMenuId(null);
    if (isPublishJobConflict(error)) {
      setRowActionError(error.message);
      if (error.publishJob) onJobStatusChange(questionId, error.publishJob);
      await refetchQuestionPage();
      return;
    }
    setRowActionError(error instanceof Error ? error.message : 'The question action failed');
  };

  const usageDialogQuery = useQuestionUsageQuery(
    usageDialogQuestion?.questionDefinitionId,
    { enabled: Boolean(usageDialogQuestion) },
  );
  const usageDialogUsage = usageDialogQuery.data ?? EMPTY_QUESTION_USAGE;

  return (
    <div style={styles.page}>
      <AdminPageHeader
        title={scopeAccountName ? `${scopeAccountName} question bank` : 'Emtrain question bank'}
        description="The question bank: reusable questions that lessons pull in by reference."
        actions={(
        <div style={styles.menuAnchor} ref={menuRef}>
          <button
            type="button"
            style={styles.addLessonButton}
            onClick={() => setIsMenuOpen((current) => !current)}
            aria-haspopup="menu"
            aria-expanded={isMenuOpen}
          >
            + New Question
          </button>
          {isMenuOpen && (
            <div style={styles.menu} role="menu">
              {QUESTION_TYPES.map((blockType) => (
                <button
                  key={blockType}
                  type="button"
                  role="menuitem"
                  style={{
                    ...styles.menuItem,
                    ...(hoveredType === blockType ? styles.menuItemHovered : {}),
                  }}
                  onMouseEnter={() => setHoveredType(blockType)}
                  onMouseLeave={() => setHoveredType(null)}
                  onClick={() => handleSelectType(blockType)}
                >
                  {questionBlockTypeLabel(blockType)}
                </button>
              ))}
            </div>
          )}
        </div>
        )}
      />

      {rowActionError && <div role="alert" style={{ color: adminTokens.status.error.text, marginBottom: adminTokens.spacing.sm }}>{rowActionError}</div>}
      {rowStatusWarning && <div role="alert" style={{ display: 'flex', alignItems: 'flex-start', gap: adminTokens.spacing.sm, marginBottom: adminTokens.spacing.sm, padding: adminTokens.spacing.sm, border: `1px solid color-mix(in srgb, ${adminTokens.status.warning.text} 45%, ${adminTokens.surface.panel})`, borderRadius: adminTokens.radius.control, background: `color-mix(in srgb, ${adminTokens.status.warning.text} 9%, ${adminTokens.surface.panel})`, color: adminTokens.text.primary, fontSize: 13, lineHeight: 1.4 }}><FontAwesomeIcon icon={faTriangleExclamation} aria-hidden="true" style={{ color: adminTokens.status.warning.text }} /><span>Couldn't refresh preparation status. Showing the last known state; it will update once the connection recovers. Your unsaved edits are kept.</span></div>}

      <div style={styles.listToolbar}>
        <input
          type="text"
          style={styles.filterInput}
          placeholder="Search ID or question text..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          aria-label="Search ID or question text"
        />
        <select
          style={styles.typeSelect}
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value as QuestionBlockType | 'all');
            setPage(1);
          }}
          aria-label="Filter by question type"
        >
          {QUESTION_TYPE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <label style={styles.archivedCheckboxLabel}>
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => {
              setShowArchived(e.target.checked);
              setPage(1);
            }}
          />
          Show archived
        </label>
        <span style={styles.resultsCount}>
          {totalCount} {totalCount === 1 ? 'result' : 'results'}
        </span>
        {hasFiltersApplied && (
          <button type="button" onClick={handleResetFilters} style={styles.resetFiltersButton}>
            Reset
          </button>
        )}
      </div>

      <section style={styles.panel} aria-label="Question results">
        {isLoading || isSwitchingScope ? (
          <p style={styles.stateText}>Loading questions...</p>
        ) : isError ? (
          <p style={styles.stateText}>Failed to load questions.</p>
        ) : totalCount === 0 && !queryParams.search && typeFilter === 'all' && !showArchived ? (
          <p style={styles.stateText}>No questions yet. Create one to get started.</p>
        ) : totalCount === 0 ? (
          <p style={styles.stateText}>No questions match your search or filter.</p>
        ) : (
          <div style={styles.tableScroll}>
            <table style={styles.table}>
              <colgroup>
                <col style={{ width: '64px' }} />
                <col />
                <col style={{ width: '20%' }} />
                {scopeAccountId === null && <col style={{ width: '10%' }} />}
                <col style={{ width: '14%' }} />
                <col style={{ width: '64px' }} />
              </colgroup>
              <thead>
                <tr>
                  <SortableHeader
                    label="ID"
                    sortKey="id"
                    activeKey={sortKey}
                    direction={sortDir}
                    onSort={handleSort}
                    styles={styles}
                  />
                  <SortableHeader
                    label="Question text"
                    sortKey="questionText"
                    activeKey={sortKey}
                    direction={sortDir}
                    onSort={handleSort}
                    styles={styles}
                  />
                  <SortableHeader
                    label="Type"
                    sortKey="type"
                    activeKey={sortKey}
                    direction={sortDir}
                    onSort={handleSort}
                    styles={styles}
                  />
                  {scopeAccountId === null && (<SortableHeader
                    label="Public"
                    sortKey="public"
                    activeKey={sortKey}
                    direction={sortDir}
                    onSort={handleSort}
                    styles={styles}
                    headerStyle={styles.headerCellCentered}
                  />)}
                  <SortableHeader
                    label="Used in"
                    sortKey="usedIn"
                    activeKey={sortKey}
                    direction={sortDir}
                    onSort={handleSort}
                    styles={styles}
                    headerStyle={styles.headerCellRight}
                  />
                  <th style={styles.questionHeaderCellActions} aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {questions.map((question) => {
                  const usageCount = question.usageCount;
                  const isRowMenuOpen = openRowMenuId === question.questionDefinitionId;
                  const isArchived = Boolean(question.retiredAt);
                const job = currentRowJob(question.publishJob, rowJobs[question.questionDefinitionId]);
                const isPreparing = isActivePublishJob(job);
                const statusLabel = job?.status === 'queued' ? 'Queued'
                  : isPreparing ? job?.currentStep === 'translation' ? 'Translating' : job?.currentStep === 'narration' ? 'Generating audio' : 'Preparing'
                  : job?.status === 'failed' ? job.currentStep === 'translation' ? 'Translation failed' : job.currentStep === 'narration' ? 'Audio failed' : 'Preparation failed'
                    : null;
                const progress = job?.status === 'running' && job.currentStep && (job.progress?.total ?? 0) > 0 ? job.progress : null;
                const completed = progress ? Math.max(0, Math.min(progress.completed, progress.total)) : null;
                const displayStatus = statusLabel && progress && completed !== null
                  ? `${statusLabel} · ${completed} of ${progress.total} languages`
                  : statusLabel;
                const statusTone = job?.status === 'failed'
                  ? { color: adminTokens.status.error.text, background: adminTokens.status.error.background, border: adminTokens.status.error.border }
                  : job?.status === 'running'
                    ? { color: adminTokens.text.primary, background: `color-mix(in srgb, ${adminTokens.action.primary} 9%, ${adminTokens.surface.panel})`, border: adminTokens.focus.ring }
                    : { color: adminTokens.text.secondary, background: adminTokens.surface.panel, border: adminTokens.border.subtle };
                  return (
                    <tr key={question.questionDefinitionId} style={styles.tableRow}>
                    <td style={{ ...styles.tdId, ...(isArchived ? styles.tdArchivedText : {}) }}>
                      {formatQuestionHumanId(question.questionDefinitionId)}
                    </td>
                    <td style={styles.tdText}>
                      <div style={styles.questionTextCellContent}>
                        <button
                          type="button"
                          style={
                            isArchived
                              ? styles.textArchived
                              : styles.textLink
                          }
                          onClick={() => handleEditQuestion(question)}
                          disabled={isPlaceholderData}
                        >
                          {/* Always the English source text, never the translation:
                              search and sort run on English columns and authors work
                              in English, so a localized row would be unsearchable and
                              unsortable by what it displays. The badge carries the
                              translation state instead. */}
                          {(question.englishSource ?? question).questionTextPlain || 'Untitled question'}
                        </button>
                        {selectedLocale !== 'en' && question.translation && (
                          <span style={styles.translationStatusContainer}>
                            <TranslationStatusBadge status={question.translation.status} showPendingLabel={false} />
                          </span>
                        )}
                      {job && job.status !== 'completed' && displayStatus && (
                        <span aria-label={`Preparation status: ${displayStatus}`} style={{ height: 22, marginLeft: adminTokens.spacing.sm, padding: `0 ${adminTokens.spacing.sm}`, border: `1px solid ${statusTone.border}`, borderRadius: 11, background: statusTone.background, color: statusTone.color, display: 'inline-flex', alignItems: 'center', gap: adminTokens.spacing.xs, whiteSpace: 'nowrap', fontSize: 12, fontWeight: 600 }}>
                          {(job.status === 'queued' || isPreparing) && <FontAwesomeIcon icon={faSpinner} spin aria-hidden="true" style={{ color: isPreparing ? adminTokens.action.primary : adminTokens.text.secondary }} />}
                          {job.status === 'failed' && <FontAwesomeIcon icon={faCircleExclamation} aria-hidden="true" />}
                          {displayStatus}
                        </span>
                      )}
                      </div>
                    </td>
                    <td style={{ ...styles.tdType, ...(isArchived ? styles.tdArchivedText : {}) }}>
                      {questionBlockTypeLabel(question.blockType)}
                      {isArchived && <span style={styles.archivedPill}>Archived</span>}
                    </td>
                    {scopeAccountId === null && <td style={styles.tdPublic}>{question.isPublic ? '✓' : ''}</td>}
                    <td style={styles.tdUsage}>
                      {usageCount > 0 ? (
                        <button
                          type="button"
                          style={styles.usageLinkButton}
                          onClick={() => handleUsageCellClick(question)}
                          disabled={isPlaceholderData}
                        >
                          {usageCount} {usageCount === 1 ? 'lesson' : 'lessons'}
                        </button>
                      ) : '—'}
                    </td>
                    <td style={styles.tdActions}>
                      <div style={styles.rowMenuAnchor} ref={isRowMenuOpen ? rowMenuRef : undefined}>
                        <button
                          type="button"
                          style={styles.rowMenuButton}
                          onClick={() => setOpenRowMenuId(isRowMenuOpen ? null : question.questionDefinitionId)}
                          disabled={isPlaceholderData || isPreparing}
                          aria-haspopup="menu"
                          aria-expanded={isRowMenuOpen}
                          aria-label="Question actions"
                        >
                          <AdminIcon icon={adminActionIcons.more} />
                        </button>
                        {isRowMenuOpen && !isPreparing && (
                          <AdminRowMenu style={styles.rowMenu} role="menu">
                            <button
                              type="button"
                              role="menuitem"
                              style={{
                                ...styles.rowMenuItem,
                                ...(hoveredRowMenuItemId === `${question.questionDefinitionId}-edit` ? styles.rowMenuItemHovered : {}),
                              }}
                              onMouseEnter={() => setHoveredRowMenuItemId(`${question.questionDefinitionId}-edit`)}
                              onMouseLeave={() => setHoveredRowMenuItemId(null)}
                              onClick={() => handleEditQuestion(question)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              style={{
                                ...styles.rowMenuItem,
                                ...(hoveredRowMenuItemId === `${question.questionDefinitionId}-duplicate` ? styles.rowMenuItemHovered : {}),
                              }}
                              onMouseEnter={() => setHoveredRowMenuItemId(`${question.questionDefinitionId}-duplicate`)}
                              onMouseLeave={() => setHoveredRowMenuItemId(null)}
                              onClick={() => handleDuplicateQuestion(question)}
                            >
                              Duplicate
                            </button>
                            {isArchived ? (
                              <button
                                type="button"
                                role="menuitem"
                                style={{
                                  ...styles.rowMenuItem,
                                  ...(hoveredRowMenuItemId === `${question.questionDefinitionId}-unarchive` ? styles.rowMenuItemHovered : {}),
                                }}
                                onMouseEnter={() => setHoveredRowMenuItemId(`${question.questionDefinitionId}-unarchive`)}
                                onMouseLeave={() => setHoveredRowMenuItemId(null)}
                                onClick={() => { void handleUnarchiveQuestion(question); }}
                              >
                                Unarchive
                              </button>
                            ) : (usageCount > 0 ? (
                              <button
                                type="button"
                                role="menuitem"
                                style={{
                                  ...styles.rowMenuItem,
                                  ...(hoveredRowMenuItemId === `${question.questionDefinitionId}-archive` ? styles.rowMenuItemHovered : {}),
                                }}
                                onMouseEnter={() => setHoveredRowMenuItemId(`${question.questionDefinitionId}-archive`)}
                                onMouseLeave={() => setHoveredRowMenuItemId(null)}
                                onClick={() => { setOpenRowMenuId(null); setArchiveTarget(question); }}
                              >
                                Archive
                              </button>
                            ) : (
                              <button
                                type="button"
                                role="menuitem"
                                style={{
                                  ...styles.rowMenuItem,
                                  ...styles.rowMenuItemDelete,
                                  ...(hoveredRowMenuItemId === `${question.questionDefinitionId}-delete` ? styles.rowMenuItemDeleteHovered : {}),
                                }}
                                onMouseEnter={() => setHoveredRowMenuItemId(`${question.questionDefinitionId}-delete`)}
                                onMouseLeave={() => setHoveredRowMenuItemId(null)}
                                onClick={() => { setOpenRowMenuId(null); setDeleteTarget(question); }}
                              >
                                Delete
                              </button>
                            ))}
                          </AdminRowMenu>
                        )}
                      </div>
                    </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {!isLoading && !isError && totalCount > 0 && (
        <ListPagination
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          isPlaceholderData={isPlaceholderData}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      )}

      {createType && (
        <QuestionCreatePanel
          blockType={createType}
          isOpen={Boolean(createType)}
          onClose={handlePanelClose}
          onCreated={handleCreated}
        />
      )}

      {editingQuestion && (
        <QuestionCreatePanel
          key={`${editingQuestion.questionDefinitionId}:${createdEditor ? 'en' : selectedLocale}`}
          blockType={editingQuestion.blockType}
          isOpen={Boolean(editingQuestion)}
          onClose={() => setEditingQuestion(null)}
          onCreated={() => setEditingQuestion(null)}
          existingQuestion={editingQuestion}
          initialAcceptedJob={createdEditor ? editingQuestion.publishJob : undefined}
          selectedLocale={createdEditor ? 'en' : selectedLocale}
          selectedLanguageLabel={createdEditor ? 'English (US)' : selectedLanguageLabel}
          languageOptions={languageOptions}
          onJobStatusChange={onJobStatusChange}
        />
      )}

      {duplicateTarget && (
        <QuestionCreatePanel
          blockType={duplicateTarget.blockType}
          isOpen={Boolean(duplicateTarget)}
          onClose={() => setDuplicateTarget(null)}
          onCreated={handleDuplicateCreated}
          duplicateFromQuestion={duplicateTarget}
        />
      )}

      {archiveTarget && (
        <div style={styles.dialogOverlay} role="presentation">
          <div
            style={styles.dialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="archive-question-dialog-title"
            aria-describedby="archive-question-dialog-description"
          >
            <h2 id="archive-question-dialog-title" style={styles.dialogTitle}>Archive question?</h2>
            <p id="archive-question-dialog-description" style={styles.dialogBody}>
              This question will no longer be available to add to new lessons. Existing lessons that use it won't be affected.
            </p>
            <div style={styles.dialogFooter}>
              <button
                type="button"
                style={styles.secondaryButton}
                onClick={() => setArchiveTarget(null)}
                disabled={archiveMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                style={{ ...styles.archiveButton, ...(archiveMutation.isPending ? styles.buttonDisabled : {}) }}
                onClick={() => { void handleConfirmArchive(); }}
                disabled={archiveMutation.isPending}
              >
                {archiveMutation.isPending ? 'Archiving…' : 'Archive'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div style={styles.dialogOverlay} role="presentation">
          <div
            style={styles.dialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-question-dialog-title"
            aria-describedby="delete-question-dialog-description"
          >
            <h2 id="delete-question-dialog-title" style={styles.dialogTitle}>Delete question?</h2>
            <p id="delete-question-dialog-description" style={styles.dialogBody}>This cannot be undone.</p>
            <div style={styles.dialogFooter}>
              <button
                type="button"
                style={styles.secondaryButton}
                onClick={() => setDeleteTarget(null)}
                disabled={deleteMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                style={{ ...styles.deleteButton, ...(deleteMutation.isPending ? styles.buttonDisabled : {}) }}
                onClick={() => { void handleConfirmDelete(); }}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <QuestionUsagePanel
        isOpen={Boolean(usageDialogQuestion)}
        onClose={() => setUsageDialogQuestion(null)}
        questionText={usageDialogQuestion?.questionTextPlain ?? ''}
        usage={usageDialogUsage}
      />
    </div>
  );
}

QuestionsPage.displayName = 'QuestionsPage';

function createStyles(narrow: boolean): Record<string, CSSProperties> {
  const listStyles = createAdminListStyles({ narrow });
  const a = adminTokens;
  const truncatedQuestionTitle: CSSProperties = {
    display: 'block',
    width: '100%',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    fontFamily: 'inherit',
  };

  return {
    ...listStyles,
    menuAnchor: { position: 'relative' },
    menu: {
      position: 'absolute',
      top: 'calc(100% + 6px)',
      right: 0,
      width: '220px',
      backgroundColor: a.surface.panel,
      border: `1px solid ${a.border.subtle}`,
      borderRadius: a.radius.tile,
      boxShadow: a.shadow.overlay,
      zIndex: 10,
      display: 'flex',
      flexDirection: 'column',
      padding: a.spacing.xs,
    },
    menuItem: {
      ...listStyles.actionMenuItem,
      background: 'none',
      transition: 'background-color 150ms ease',
    },
    menuItemHovered: {
      backgroundColor: a.surface.canvas,
    },
    typeSelect: {
      ...listStyles.filterInput,
      width: 'auto',
      minWidth: '188px',
      cursor: 'pointer',
    },
    archivedCheckboxLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: a.spacing.xs,
      fontSize: a.typography.label.size,
      fontWeight: 600,
      color: a.text.primary,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    },
    table: {
      ...listStyles.table,
      tableLayout: 'fixed',
    },
    headerCellCentered: { textAlign: 'center' },
    headerCellRight: { textAlign: 'right' },
    questionHeaderCellActions: {
      ...listStyles.headerCellActions,
      width: 'auto',
    },
    tdId: {
      ...listStyles.idCell,
      verticalAlign: 'middle',
    },
    tdText: {
      ...listStyles.bodyCell,
      maxWidth: 0,
    },
    questionTextCellContent: {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      minWidth: 0,
      gap: a.spacing.sm,
    },
    textLink: {
      ...listStyles.titleButton,
      ...truncatedQuestionTitle,
      flex: 1,
      minWidth: 0,
      width: 'auto',
    },
    textArchived: {
      ...listStyles.titleButton,
      ...truncatedQuestionTitle,
      flex: 1,
      minWidth: 0,
      width: 'auto',
      fontWeight: 600,
      color: a.text.secondary,
      textDecoration: 'none',
    },
    translationStatusContainer: {
      flexShrink: 0,
      marginLeft: 'auto',
    },
    tdArchivedText: { color: a.text.secondary },
    archivedPill: {
      display: 'inline-block',
      marginLeft: a.spacing.xs,
      padding: '2px 8px',
      borderRadius: a.radius.pill,
      backgroundColor: a.surface.subtle,
      color: a.text.secondary,
      fontSize: '11px',
      fontWeight: 700,
      whiteSpace: 'nowrap',
    },
    tdType: {
      ...listStyles.bodyCell,
    },
    tdPublic: {
      ...listStyles.bodyCell,
      textAlign: 'center',
      color: a.status.success.text,
      fontWeight: 700,
    },
    tdUsage: {
      ...listStyles.bodyCell,
      textAlign: 'right',
      color: a.text.secondary,
    },
    tdActions: {
      ...listStyles.actionsCell,
      width: 'auto',
    },
    usageLinkButton: {
      ...listStyles.accountLinkButton,
      fontSize: 'inherit',
    },
    rowMenuAnchor: {
      ...listStyles.actionMenuWrapper,
    },
    rowMenuButton: {
      ...listStyles.actionMenuButton,
    },
    rowMenu: {
      ...listStyles.actionMenuPopover,
      minWidth: '140px',
    },
    rowMenuItem: {
      ...listStyles.actionMenuItem,
      transition: 'background-color 150ms ease',
    },
    rowMenuItemHovered: { backgroundColor: a.surface.canvas },
    rowMenuItemDelete: {
      ...listStyles.actionMenuItemDanger,
    },
    rowMenuItemDeleteHovered: { backgroundColor: a.status.error.background },
    archiveButton: adminButton.primary,
    deleteButton: adminButton.danger,
    buttonDisabled: { opacity: 0.6, cursor: 'not-allowed' },
    dialogOverlay: {
      ...listStyles.modalOverlay,
      zIndex: 1100,
      padding: a.spacing.xl,
    },
    dialog: {
      ...listStyles.modal,
      width: '380px',
      maxWidth: '100%',
    },
    dialogTitle: listStyles.modalTitle,
    dialogBody: { ...listStyles.modalText, margin: 0 },
    dialogFooter: { ...listStyles.modalActions, marginTop: a.spacing.lg },
    secondaryButton: adminButton.secondary,
  };
}
